import { isFinished, toolParts } from '@bridebook/web/lib/ai-planner/tool-parts';
import { TOOLS } from '@bridebook/web/lib/ai-planner/tools/registry';
import type { PlannerUIMessage } from '@bridebook/web/lib/ai-planner/types';
import { z } from 'zod';

const contactedSupplierSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  conversationId: z.string(),
  archived: z.boolean(),
});

/**
 * A venue the couple already have an inbox conversation with. A new enquiry is
 * never sent to it: the couple carry on the conversation they have.
 */
export type ContactedSupplier = z.infer<typeof contactedSupplierSchema>;

// An enquiry result naming the venues it did not enquire again.
const contactedResultSchema = z.object({
  data: z.object({ alreadyContacted: z.array(contactedSupplierSchema) }),
});

// Whether an enquiry result was refused because the couple already have a conversation.
export const isAlreadyContactedResult = (result: unknown): boolean =>
  contactedResultSchema.safeParse(result).success;

// Every venue a reply's finished enquiries did not enquire again, each once.
export const alreadyContactedInMessage = (message: PlannerUIMessage): ContactedSupplier[] => {
  const byId = new Map<string, ContactedSupplier>();
  for (const part of toolParts(message, TOOLS.executeClientActions)) {
    // A stored output is whatever was written, so each result is checked.
    const results: unknown = isFinished(part) ? part.output.results : [];
    if (!Array.isArray(results)) continue;
    for (const result of results) {
      const parsed = contactedResultSchema.safeParse(result);
      if (!parsed.success) continue;
      for (const supplier of parsed.data.data.alreadyContacted) byId.set(supplier.id, supplier);
    }
  }
  return [...byId.values()];
};
